"use client";

import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";

const STORAGE_KEY = "fathersday-banner-collapsed";

// One-shot confetti when the user clicks "Browse Father's Day Cards."
// canvas-confetti loads lazily via dynamic import so it doesn't ship in
// the initial bundle — only fetched on the interaction. Silent failure
// (confetti is non-essential); skipped entirely under reduced-motion.
const fireConfetti = async () => {
  if (typeof window === "undefined") return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  try {
    const confetti = (await import("canvas-confetti")).default;
    confetti({
      particleCount: 120,
      spread: 75,
      origin: { y: 0.65 },
      colors: [
        "#6852c0", // brand purple
        "#F47C7C", // coral
        "#F5B947", // yellow
        "#6BC4D2", // teal
        "#8DACFB", // logo blue
        "#EE4545", // logo red
      ],
      scalar: 1.1,
      ticks: 200,
    });
  } catch {
    // ignored — confetti is purely decorative
  }
};

// Brand purple + warm accents (peach, sunny yellow, soft teal).
// Hand-rolled rather than pulling an icon library — keeps a single file edit
// for the page and lets each icon use multiple coordinated colors.
const PlayWithSparklesIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    className="h-12 w-12"
    aria-hidden="true"
  >
    <circle
      cx="24"
      cy="26"
      r="16"
      stroke="#6852c0"
      strokeWidth="2.5"
      fill="#FFFFFF"
    />
    <path
      d="M21 20 L21 32 L31 26 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M40 10 L40 16 M37 13 L43 13"
      stroke="#F5B947"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M8 8 L8 12 M6 10 L10 10"
      stroke="#F5B947"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="42" cy="32" r="1.5" fill="#F5B947" />
  </svg>
);

const PolaroidCameraIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    className="h-12 w-12"
    aria-hidden="true"
  >
    <rect
      x="6"
      y="14"
      width="36"
      height="28"
      rx="3"
      fill="#FFFFFF"
      stroke="#6852c0"
      strokeWidth="2.5"
    />
    <rect
      x="16"
      y="9"
      width="14"
      height="6"
      rx="1.5"
      fill="#FFFFFF"
      stroke="#6852c0"
      strokeWidth="2"
    />
    <circle
      cx="24"
      cy="28"
      r="8"
      fill="#6BC4D2"
      stroke="#6852c0"
      strokeWidth="2.5"
    />
    <circle
      cx="24"
      cy="28"
      r="3.5"
      fill="#FFFFFF"
      stroke="#6852c0"
      strokeWidth="1.5"
    />
    <rect x="33" y="18" width="5" height="3.5" rx="0.5" fill="#F5B947" />
  </svg>
);

// Three ties hanging on a rack, each in a different classic pattern
// (stripes / dots / pinstripe). White outline + warm fills so they pop on the
// bg-wg-purple hero. Bulky Windsor-style knot makes the shape read instantly.
// Each tie pivots from its own knot (origin-top on its container) and the
// sway is staggered via animationDelay so the three ties move in coordinated
// but not synchronized phase — feels like wind, not a metronome.
type TieVariant = "stripes" | "dots" | "pinstripe";

const TieIcon = ({ variant }: { variant: TieVariant }) => {
  const bodyColor =
    variant === "stripes"
      ? "#F47C7C"
      : variant === "dots"
        ? "#6BC4D2"
        : "#F5B947";
  const knotColor = variant === "stripes" ? "#F5B947" : "#F47C7C";

  return (
    <svg
      viewBox="0 0 40 88"
      fill="none"
      className="h-14 w-6 md:h-16 md:w-7 lg:h-20 lg:w-8"
      aria-hidden="true"
    >
      <path
        d="M20 0 L30 5 L33 14 L28 22 L12 22 L7 14 L10 5 Z"
        fill={knotColor}
        stroke="#FFFFFF"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M12 22 L28 22 L36 70 L20 86 L4 70 Z"
        fill={bodyColor}
        stroke="#FFFFFF"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      {variant === "stripes" && (
        <>
          <line x1="9" y1="36" x2="31" y2="44" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="6" y1="50" x2="34" y2="58" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="5" y1="64" x2="35" y2="72" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
        </>
      )}
      {variant === "dots" && (
        <>
          <circle cx="14" cy="36" r="2" fill="#FFFFFF" />
          <circle cx="26" cy="42" r="2" fill="#FFFFFF" />
          <circle cx="14" cy="50" r="2" fill="#FFFFFF" />
          <circle cx="26" cy="56" r="2" fill="#FFFFFF" />
          <circle cx="14" cy="64" r="2" fill="#FFFFFF" />
          <circle cx="26" cy="70" r="2" fill="#FFFFFF" />
        </>
      )}
      {variant === "pinstripe" && (
        <line
          x1="20"
          y1="28"
          x2="20"
          y2="78"
          stroke="#FFFFFF"
          strokeWidth="1.5"
          strokeDasharray="3 4"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
};

// Open tie box. 3:2 proportions so it reads as a gift box, not a coffin.
// Light blue body, white outline, vivid red ribbon at the 1/3 mark.
const GiftBoxBody = () => (
  <svg
    viewBox="0 0 60 40"
    fill="none"
    className="block h-10 w-16 md:h-12 md:w-20 lg:h-14 lg:w-24"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="10"
      width="54"
      height="28"
      rx="2"
      fill="#8DACFB"
      stroke="#FFFFFF"
      strokeWidth="2"
    />
    <rect x="16" y="10" width="9" height="28" fill="#EE4545" />
  </svg>
);

// Lid with prominent two-loop bow above it centered over the ribbon. The
// loops are ellipses (rather than triangles) so the bow reads as a real bow
// instead of an arrow. ViewBox is taller than the lid body alone to leave
// the bow room above.
const GiftBoxLid = () => (
  <svg
    viewBox="0 0 60 22"
    fill="none"
    className="block h-5 w-16 md:h-6 md:w-20 lg:h-7 lg:w-24"
    aria-hidden="true"
  >
    {/* Bow loops centered over ribbon (~x=20) */}
    <ellipse
      cx="14"
      cy="6"
      rx="7"
      ry="4.5"
      fill="#EE4545"
      stroke="#FFFFFF"
      strokeWidth="0.8"
    />
    <ellipse
      cx="26"
      cy="6"
      rx="7"
      ry="4.5"
      fill="#EE4545"
      stroke="#FFFFFF"
      strokeWidth="0.8"
    />
    {/* Center knot covering where loops meet */}
    <rect
      x="17"
      y="2"
      width="6"
      height="9"
      rx="0.5"
      fill="#CD5959"
      stroke="#FFFFFF"
      strokeWidth="0.6"
    />
    {/* Lid body */}
    <rect
      x="2"
      y="11"
      width="56"
      height="9"
      rx="2"
      fill="#B5C9FF"
      stroke="#FFFFFF"
      strokeWidth="2"
    />
    {/* Ribbon continues across lid */}
    <rect x="16" y="11" width="9" height="9" fill="#EE4545" />
  </svg>
);

// Mixed-shape confetti burst (diamond + dots + a star) in the brand-logo
// palette. Fires once when the lid lands on the box.
const ClosingSparkles = () => (
  <svg
    viewBox="0 0 80 80"
    fill="none"
    className="h-full w-full"
    aria-hidden="true"
  >
    <path
      d="M40 14 L43 24 L53 27 L43 30 L40 40 L37 30 L27 27 L37 24 Z"
      fill="#F5B947"
    />
    <path d="M18 30 L21 33 L18 36 L15 33 Z" fill="#8DACFB" />
    <path d="M62 32 L65 35 L62 38 L59 35 Z" fill="#F47C7C" />
    <path d="M22 60 L24 62 L22 64 L20 62 Z" fill="#F47C7C" />
    <path d="M58 58 L60 60 L58 62 L56 60 Z" fill="#8DACFB" />
    <circle cx="10" cy="40" r="2" fill="#F5B947" />
    <circle cx="70" cy="42" r="2" fill="#F5B947" />
    <circle cx="40" cy="68" r="2" fill="#F47C7C" />
    <circle cx="48" cy="10" r="1.5" fill="#8DACFB" />
    <circle cx="32" cy="12" r="1.5" fill="#F47C7C" />
  </svg>
);

// Catch box, positioned at the bottom of the TieRack container so the
// falling tie drops straight down into it (no lateral motion needed).
//   - outer wrapper: opacity lifecycle (gift-box-show)
//   - lid: hidden until the tie lands, then fades in tilted-open and snaps
//     down onto the box with a small overshoot
//   - sparkles: burst at the moment of close
// opacity-0 is the default so reduced-motion users never see the box.
const CatchBox = () => (
  <div className="relative opacity-0 motion-safe:animate-gift-box-show">
    <GiftBoxBody />
    <div className="absolute -top-2.5 left-0 w-full origin-bottom opacity-0 motion-safe:animate-gift-lid-close md:-top-3 lg:-top-3.5">
      <GiftBoxLid />
    </div>
    <div className="pointer-events-none absolute -inset-6 opacity-0 motion-safe:animate-gift-sparkle-pop">
      <ClosingSparkles />
    </div>
  </div>
);

// TieRack composition: rack bar + 3 ties hanging + CatchBox at the bottom.
// Container is h-80 (320px) across all breakpoints so the box's bottom
// aligns with where the Browse CTA button sits — mobile's text wraps and
// pushes the button down further than md/lg, so a single tall height
// works better than per-breakpoint variants. The middle tie falls pure-
// vertical: drops down, rotates horizontal above the box, then continues
// down into the box and fades as it crosses the threshold.
const TieRack = () => (
  <div className="relative h-80 w-28 md:w-36 lg:w-44">
    {/* Rack bar + end caps (translucent white so it reads against bg-wg-purple) */}
    <div className="absolute left-0 right-0 top-2 h-1.5 rounded-full bg-white/85" />
    <div className="absolute left-0 top-0.5 h-4 w-1.5 rounded-full bg-white/85" />
    <div className="absolute right-0 top-0.5 h-4 w-1.5 rounded-full bg-white/85" />

    <div
      className="absolute left-[10%] top-1.5 origin-top motion-safe:animate-gentle-sway"
      style={{ animationDelay: "0s" }}
    >
      <TieIcon variant="stripes" />
    </div>
    {/* Middle tie falls straight down into the box below (3s after load,
        one-shot, motion-safe). Outer div handles centering, middle handles
        the fall, inner keeps swaying so it wiggles with momentum. */}
    <div className="absolute left-1/2 top-1.5 -translate-x-1/2">
      <div className="motion-safe:animate-tie-fall">
        <div
          className="origin-top motion-safe:animate-gentle-sway"
          style={{ animationDelay: "-1.6s" }}
        >
          <TieIcon variant="dots" />
        </div>
      </div>
    </div>
    <div
      className="absolute right-[10%] top-1.5 origin-top motion-safe:animate-gentle-sway"
      style={{ animationDelay: "-3.2s" }}
    >
      <TieIcon variant="pinstripe" />
    </div>

    {/* Catch box sits at the bottom of the rack container, centered
        horizontally under the middle tie. Renders LAST so it stacks above
        the falling tie — the tie visually disappears behind it as it drops
        into the box. */}
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
      <CatchBox />
    </div>
  </div>
);

const HeartIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-7 w-7 flex-shrink-0 md:h-8 md:w-8"
    aria-hidden="true"
  >
    <path
      d="M16 27 C4 18, 4 9, 11 9 C14 9, 16 12, 16 12 C16 12, 18 9, 21 9 C28 9, 28 18, 16 27 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="5" cy="6" r="1.5" fill="#F5B947" />
    <circle cx="27" cy="6" r="1.5" fill="#F5B947" />
  </svg>
);

// Paintbrush diagonal from lower-left to upper-right (held painting up
// like on an easel). Cream handle, yellow ferrule, coral triangular
// bristles tapering to a point. A substantial coral brush stroke arcs
// across the top of the icon, matching the bristle color — the visible
// mark the brush has just painted. For the "Real Artists" trust signal.
const PaintbrushIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-8 w-8 flex-shrink-0"
    aria-hidden="true"
  >
    {/* Painted brush stroke arcing above the brush */}
    <path
      d="M4 10 Q 16 2, 28 9"
      stroke="#F47C7C"
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
    />
    {/* Brush handle */}
    <path
      d="M3 28 L6 31 L17 20 L14 17 Z"
      fill="#FFFCEB"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    {/* Ferrule (metal band) */}
    <path
      d="M14 17 L17 20 L20 17 L17 14 Z"
      fill="#F5B947"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    {/* Bristles tapering to point */}
    <path
      d="M17 14 L20 17 L24 11 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

// Coupon with dashed perforation down the left, a yellow plus-mark in the
// tear-off section, and a coral heart in the main field. For the "Free to
// Start" trust signal.
const CouponIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-8 w-8 flex-shrink-0"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="9"
      width="26"
      height="14"
      rx="1.5"
      fill="#FFFFFF"
      stroke="#6852c0"
      strokeWidth="1.5"
    />
    <line
      x1="11"
      y1="10"
      x2="11"
      y2="22"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeDasharray="2 2"
      strokeLinecap="round"
    />
    <path
      d="M7 13 L7 17 M5 15 L9 15"
      stroke="#F5B947"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M20 17 C18 15.5, 15.5 16, 15.5 18 C15.5 20.5, 20 22, 20 22 C20 22, 24.5 20.5, 24.5 18 C24.5 16, 22 15.5, 20 17 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="1"
      strokeLinejoin="round"
    />
  </svg>
);

// Two-leaf sprout rising from a soil curve, with a small yellow sun above.
// Soft green (#A8E0B5) is a one-off color here — "growth" wants green and
// teal-leaves would read odd. For the "Zero Waste" trust signal.
const SproutIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-8 w-8 flex-shrink-0"
    aria-hidden="true"
  >
    <circle cx="26" cy="6" r="2" fill="#F5B947" stroke="#6852c0" strokeWidth="1" />
    <path
      d="M16 26 Q9 22 7 14 Q13 14 16 23 Z"
      fill="#A8E0B5"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M16 26 Q23 22 25 14 Q19 14 16 23 Z"
      fill="#A8E0B5"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <line
      x1="16"
      y1="26"
      x2="16"
      y2="22"
      stroke="#6852c0"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M5 28 Q16 25 27 28"
      stroke="#6852c0"
      strokeWidth="1.5"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const SparkleBurstIcon = () => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    className="h-7 w-7 flex-shrink-0 md:h-8 md:w-8"
    aria-hidden="true"
  >
    <path
      d="M16 3 L18 13 L28 16 L18 19 L16 29 L14 19 L4 16 L14 13 Z"
      fill="#F5B947"
      stroke="#6852c0"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="5" cy="5" r="1.5" fill="#F47C7C" />
    <circle cx="27" cy="5" r="1.5" fill="#F47C7C" />
    <circle cx="5" cy="27" r="1.5" fill="#6BC4D2" />
    <circle cx="27" cy="27" r="1.5" fill="#6BC4D2" />
  </svg>
);

const PaperPlaneIcon = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    className="h-12 w-12"
    aria-hidden="true"
  >
    <path
      d="M42 8 L6 22 L20 27 L42 8 Z"
      fill="#FFFFFF"
      stroke="#6852c0"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M42 8 L20 27 L24 40 L42 8 Z"
      fill="#F47C7C"
      stroke="#6852c0"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M4 32 L12 32"
      stroke="#F5B947"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <path
      d="M8 38 L18 38"
      stroke="#F5B947"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <path
      d="M2 26 L8 26"
      stroke="#F5B947"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

interface FathersDayNarrativeBannerProps {
  onStartCreating?: () => void;
}

export default function FathersDayNarrativeBanner({
  onStartCreating,
}: FathersDayNarrativeBannerProps) {
  const searchParams = useSearchParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const storedValue = localStorage.getItem(STORAGE_KEY);
    const hasCategory = searchParams.get("category");
    const hasSearch = searchParams.get("search");

    if (hasCategory || hasSearch || storedValue === "true") {
      setIsCollapsed(true);
    }

    setIsInitialized(true);
  }, [searchParams]);

  useEffect(() => {
    if (!isInitialized || typeof window === "undefined") return;

    const hasCategory = searchParams.get("category");
    const hasSearch = searchParams.get("search");

    if (hasCategory || hasSearch) {
      setIsCollapsed(true);
      localStorage.setItem(STORAGE_KEY, "true");
    }
  }, [searchParams, isInitialized]);

  // Schedule the tie-fall sound effect (gentle thud + applause) to play
  // synced with the visual landing. The file's thud is at ~0.1s in, so we
  // start playback at 6.7s after mount — the tie's visual landing happens
  // at ~6.8s (95% of the 4s tie-fall, which has a 3s start delay). The
  // applause that follows lines up naturally with the lid closing.
  //
  // Browser autoplay policy blocks audio that's not tied to a user gesture.
  // To work around this, we pre-create the audio element on mount and
  // "prime" it on the FIRST user interaction (pointerdown/keydown/touchstart)
  // by doing a silent muted play+pause — that unlocks the element so a
  // later programmatic play() isn't blocked. If the user hasn't interacted
  // by the scheduled time, the play() is silently skipped (the .catch
  // swallows the failure).
  //
  // Skipped entirely under prefers-reduced-motion. Re-runs when the banner
  // expands (the catch animation restarts then).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isCollapsed) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
      return;

    const audio = new Audio("/sounds/tie-fall.mp3");
    audio.volume = 0.4;
    audio.preload = "auto";

    // Prime audio on first user interaction so the scheduled play isn't
    // blocked by autoplay policy. Muted play+pause unlocks the element.
    let primed = false;
    const primeAudio = () => {
      if (primed) return;
      primed = true;
      audio.muted = true;
      void audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        })
        .catch(() => {
          audio.muted = false;
        });
    };
    const events: Array<keyof DocumentEventMap> = [
      "pointerdown",
      "keydown",
      "touchstart",
    ];
    events.forEach((e) =>
      document.addEventListener(e, primeAudio, {
        once: true,
        passive: true,
      }),
    );

    const timer = window.setTimeout(() => {
      audio.play().catch(() => {
        // User never interacted with the page; autoplay blocked. Silent.
      });
    }, 6700);

    return () => {
      window.clearTimeout(timer);
      events.forEach((e) => document.removeEventListener(e, primeAudio));
      audio.pause();
      audio.src = "";
    };
  }, [isCollapsed]);

  const handleStartCreating = () => {
    setIsCollapsed(true);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "true");
    }
    void fireConfetti();
    onStartCreating?.();
  };

  const handleLearnMore = () => {
    setIsCollapsed(false);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, "false");
    }
  };

  const handleFaqAnchorClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (typeof window === "undefined") return;
    const target = document.getElementById("faq");
    if (!target) return;
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  if (isCollapsed) {
    return (
      <>
        <section className="bg-wg-purple text-white">
          <div className="mx-auto flex max-w-4xl flex-col items-start justify-between gap-3 px-6 py-4 sm:flex-row sm:items-center">
            <h1 className="text-xl font-bold md:text-2xl">
              Father&apos;s Day eCards — Sunday, June 21, 2026
            </h1>
            <button
              onClick={handleLearnMore}
              aria-expanded="false"
              aria-label="Learn more about Father's Day eCards"
              className="flex items-center gap-1 text-sm font-medium text-white/90 transition-colors hover:text-white"
            >
              Learn more
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-wg-purple text-white">
        {/* Tie rack lives in the top-right corner of the hero (its original
            home) — directly below the navbar's Get Started Free button.
            The middle tie drops vertically into the CatchBox at the bottom of
            the rack (one-shot, motion-safe). */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-2 top-0 md:right-8 lg:right-16"
        >
          <TieRack />
        </div>
        <div className="mx-auto max-w-4xl px-6 py-12 text-center">
          <h1 className="mb-4 text-4xl font-bold md:text-5xl">
            Father&apos;s Day eCards
          </h1>
          <p className="mb-2 text-lg text-white/90 md:text-xl">
            Animated cards for dads, stepdads, granddads, and the father figures
            who showed up. Hand-crafted by real artists, sent in seconds, with
            room for your own photos and words.
          </p>
          <p className="mb-8 text-base text-white/80">
            Father&apos;s Day 2026 is <strong>Sunday, June 21</strong>. Digital
            cards land the morning of — no shipping window to miss.
          </p>
          <button
            onClick={handleStartCreating}
            className="inline-block rounded-lg bg-white px-6 py-2 text-lg font-semibold text-wg-purple transition-colors hover:bg-gray-100"
          >
            Browse Father&apos;s Day Cards
          </button>
          <p className="mt-6 text-sm text-white/85">
            <a
              href="#faq"
              onClick={handleFaqAnchorClick}
              className="inline-flex items-center gap-1 underline decoration-white/40 underline-offset-4 transition-colors hover:text-white hover:decoration-white"
            >
              Not sure what to write? Have a question? Read the FAQs
              <span aria-hidden="true">↓</span>
            </a>
          </p>
        </div>
      </section>

      {/* Why an animated card */}
      <section className="bg-wg-bg py-12">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="mb-8 flex flex-wrap items-center justify-center gap-3 text-center text-2xl font-bold text-wg-purple md:gap-4 md:text-3xl">
            <SparkleBurstIcon />
            <span>Why Send an Animated Father&apos;s Day Card?</span>
            <SparkleBurstIcon />
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4">
                <PlayWithSparklesIcon />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Movement Carries Feeling
              </h3>
              <p className="text-[#4a4a4a]">
                A still card says the words. An animated one delivers the
                feeling — perfect when you can&apos;t be in the room to hand it
                over yourself.
              </p>
            </div>
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4">
                <PolaroidCameraIcon />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Add Your Photos &amp; Voice
              </h3>
              <p className="text-[#4a4a4a]">
                Drop in the photo from that fishing trip, the video of the
                grandkids, the message in your own voice. Generic cards skip
                this part. We don&apos;t.
              </p>
            </div>
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4">
                <PaperPlaneIcon />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Arrives in Seconds
              </h3>
              <p className="text-[#4a4a4a]">
                No shipping cutoffs. Send it the morning of Father&apos;s Day —
                or the afternoon of, when you suddenly remember. Both work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip — light EEAT signals. H2 gives the three H3s below a
          proper hierarchy parent so the document outline reads cleanly for
          screen readers and AI Overview ingestion. */}
      <section className="bg-wg-bg py-10">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="mb-8 flex flex-wrap items-center justify-center gap-3 text-center text-2xl font-bold text-wg-purple md:gap-4 md:text-3xl">
            <HeartIcon />
            <span>What Makes Us Different</span>
            <HeartIcon />
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <PaintbrushIcon />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                  Designed by Real Artists
                </h3>
                <p className="text-[#4a4a4a]">
                  Every animation is handcrafted — not auto-generated. Our
                  artists draw, animate, and revise so the moment lands.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CouponIcon />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                  Free to Start
                </h3>
                <p className="text-[#4a4a4a]">
                  Browse the Father&apos;s Day collection, customize a card,
                  and send it without a paywall in the way of the moment.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <SproutIcon />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                  Zero Waste, Same Warmth
                </h3>
                <p className="text-[#4a4a4a]">
                  No paper, no envelope, no landfill — just the message and
                  the smile. A nice touch for the dad who&apos;s been
                  decluttering.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
